import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Code, Bot, Cpu, Terminal, Package, FileCode, PlusCircle, MinusCircle, RotateCcw } from 'lucide-react';

// --- DATA SOURCE ---
// All tutorial content is sourced from this JSON object.
const tutorialData = [
  {
    id: 'intro',
    title: '1. The Goal: A Simple Counter',
    content: "In this tutorial, we'll build the 'Hello, World!' of interactive React applications: a simple counter. This will teach us the most fundamental hook in React, `useState`, which allows us to add state to our components.",
    code: `// We will build a simple but powerful counter component.`
  },
  {
    id: 'setup',
    title: '2. Project Setup',
    content: "First, let's get a fresh React project running. We'll use Vite for a fast and modern setup. Open your terminal and run these commands.",
    code: `mkdir react-counter
cd react-counter
npm create vite@latest . -- --template react
npm install
npm run dev`
  },
  {
    id: 'cleanup',
    title: '3. Basic App Component',
    content: "Let's start with a clean slate. Open `src/App.jsx` and replace its contents with a basic functional component. We'll add a `main` element and a title for our app.",
    code: `import './App.css';

function App() {
  return (
    <main>
      <h1>React Counter</h1>
    </main>
  );
}

export default App;`
  },
  {
    id: 'add-state',
    title: '4. Introducing `useState`',
    content: "To make our counter work, it needs to 'remember' the current count. For this, we use the `useState` hook. We'll import it from React and call it to create a new piece of state. We'll name our state variable `count` and give it an initial value of 0.",
    code: `import { useState } from 'react';
import './App.css';

function App() {
  const [count, setCount] = useState(0);

  return (
    <main>
      <h1>React Counter</h1>
    </main>
  );
}

export default App;`
  },
  {
    id: 'display-state',
    title: '5. Displaying the State',
    content: "Now that we have a `count` variable in state, let's show it on the screen. We can embed any JavaScript expression directly into our JSX by wrapping it in curly braces `{}`. Let's add a `div` to display the current count.",
    code: `import { useState } from 'react';
import './App.css';

function App() {
  const [count, setCount] = useState(0);

  return (
    <main>
      <h1>React Counter</h1>
      <div className="counter-display">{count}</div>
    </main>
  );
}

export default App;`
  },
    {
    id: 'add-button',
    title: '6. Adding an Increment Button',
    content: "A counter needs a way to count! Let's add a button to our component. For now, it won't do anything, but we'll wire it up in the next step.",
    code: `import { useState } from 'react';
import './App.css';

function App() {
  const [count, setCount] = useState(0);

  return (
    <main>
      <h1>React Counter</h1>
      <div className="counter-display">{count}</div>
      <button>Increment</button>
    </main>
  );
}

export default App;`
  },
  {
    id: 'handle-click',
    title: '7. Handling Click Events',
    content: "To make the button interactive, we need an event handler. We'll create a function called `handleIncrement` and pass it to the button's `onClick` prop. This tells React to execute our function whenever the button is clicked.",
    code: `import { useState } from 'react';
import './App.css';

function App() {
  const [count, setCount] = useState(0);

  const handleIncrement = () => {
    // Logic will go here
  };

  return (
    <main>
      <h1>React Counter</h1>
      <div className="counter-display">{count}</div>
      <button onClick={handleIncrement}>Increment</button>
    </main>
  );
}

export default App;`
  },
  {
    id: 'update-state',
    title: '8. Updating the State',
    content: "Inside `handleIncrement`, we'll call `setCount`. This is the 'setter' function that `useState` gave us. We tell it what the *new* state should be. In this case, it's the current `count` plus one. Calling a setter function tells React to re-render the component with the new state value.",
    code: `import { useState } from 'react';
import './App.css';

function App() {
  const [count, setCount] = useState(0);

  const handleIncrement = () => {
    setCount(count + 1);
  };

  return (
    <main>
      <h1>React Counter</h1>
      <div className="counter-display">{count}</div>
      <button onClick={handleIncrement}>Increment</button>
    </main>
  );
}

export default App;`
  },
  {
    id: 'decrement-feature',
    title: '9. Adding a Decrement Button',
    content: "Let's add a decrement button to reinforce the pattern. We create a new function, `handleDecrement`, that subtracts one from the count, and wire it up to a new button's `onClick` event.",
    code: `import { useState } from 'react';
import './App.css';

function App() {
  const [count, setCount] = useState(0);

  const handleIncrement = () => setCount(count + 1);
  const handleDecrement = () => setCount(count - 1);

  return (
    <main>
      <h1>React Counter</h1>
      <div className="counter-display">{count}</div>
      <button onClick={handleDecrement}>Decrement</button>
      <button onClick={handleIncrement}>Increment</button>
    </main>
  );
}

export default App;`
  },
  {
    id: 'reset-feature',
    title: '10. Adding a Reset Button',
    content: "A reset button is a great addition. The `handleReset` function is the simplest of all—it just sets the count back to the initial state of 0.",
    code: `import { useState } from 'react';
import './App.css';

function App() {
  const [count, setCount] = useState(0);

  const handleIncrement = () => setCount(count + 1);
  const handleDecrement = () => setCount(count - 1);
  const handleReset = () => setCount(0);

  return (
    <main>
      <h1>React Counter</h1>
      <div className="counter-display">{count}</div>
      <button onClick={handleDecrement}>Decrement</button>
      <button onClick={handleReset}>Reset</button>
      <button onClick={handleIncrement}>Increment</button>
    </main>
  );
}

export default App;`
  },
  {
    id: 'styling',
    title: '11. Final Styling',
    content: "Our counter is fully functional! The last step is to add some basic CSS to make it look clean and presentable. We can add classNames to our elements and define their styles in `src/App.css`.",
    code: `import { useState } from 'react';
import './App.css'; // Don't forget to add styles to this file!

function App() {
  const [count, setCount] = useState(0);

  const handleIncrement = () => setCount(count + 1);
  const handleDecrement = () => setCount(count - 1);
  const handleReset = () => setCount(0);

  return (
    <main className="container">
      <h1>React Counter</h1>
      <div className="counter-display">{count}</div>
      <div className="button-group">
        <button className="btn btn-decrement" onClick={handleDecrement}>Decrement</button>
        <button className="btn btn-reset" onClick={handleReset}>Reset</button>
        <button className="btn btn-increment" onClick={handleIncrement}>Increment</button>
      </div>
    </main>
  );
}

export default App;`
  },
  {
    id: 'next-steps',
    title: '12. Conclusion & Next Steps',
    content: "Congratulations! You've built a fully functional React component using the `useState` hook. This core concept is the foundation for building much more complex applications. From here, you could try adding features like setting a custom starting value or preventing the count from going below zero.",
    code: `// Great work! You now understand the fundamentals of state in React.`
  }
];


// --- STYLES ---
const GlobalStyles = () => (
    <style>{`
    :root {
      --bg-color: #ffffff;
      --pane-bg: #f8f9fa;
      --border-color: #dee2e6;
      --text-color: #212529;
      --text-muted: #6c757d;
      --accent-color: #007bff;
      --accent-hover: #0056b3;
      --active-bg: #e9ecef;
      --code-bg: #f1f3f5;
      --code-text-color: #212529;
      --red: #dc3545;
      --green: #28a745;
      --grey: #6c757d;
    }
    .tutorial-container {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
      display: flex;
      height: 100vh;
      width: 100%;
      background-color: var(--bg-color);
      color: var(--text-color);
      overflow: hidden;
    }
    .left-pane {
      flex: 0 0 280px;
      border-right: 1px solid var(--border-color);
      padding: 24px;
      overflow-y: auto;
      background-color: var(--pane-bg);
    }
    .left-pane h2 {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 1.2rem;
      margin-top: 0;
      margin-bottom: 24px;
      color: var(--accent-color);
    }
    .left-pane ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }
    .left-pane li {
      padding: 12px 16px;
      cursor: pointer;
      border-radius: 6px;
      transition: background-color 0.2s, color 0.2s;
      margin-bottom: 4px;
      font-size: 0.95rem;
      color: var(--text-muted);
    }
    .left-pane li:hover {
      background-color: var(--active-bg);
      color: var(--text-color);
    }
    .left-pane li.active {
      background-color: var(--accent-color);
      color: white;
      font-weight: 600;
    }
    .center-pane {
      flex: 1;
      padding: 40px 60px;
      overflow-y: auto;
      scroll-behavior: smooth;
    }
    .instruction-section {
      margin-bottom: 120px;
      max-width: 700px;
    }
    .instruction-section h3 {
      font-size: 2rem;
      color: var(--accent-color);
      margin-bottom: 16px;
    }
    .instruction-section p {
      line-height: 1.7;
      font-size: 1.1rem;
      white-space: pre-wrap;
    }
    .instruction-section code {
        background-color: var(--active-bg);
        color: var(--red);
        padding: 2px 6px;
        border-radius: 4px;
        font-family: 'Fira Code', monospace;
    }
    .instruction-section pre > code {
        display: block;
        padding: 16px;
        background-color: var(--code-bg);
        color: var(--code-text-color);
    }

    .right-pane {
      flex: 1.2;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;
      background-color: var(--bg-color);
    }
    .code-container {
      width: 100%;
      height: 90%;
      background-color: var(--code-bg);
      border: 1px solid var(--border-color);
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 4px 15px rgba(0,0,0,0.05);
    }
    .code-container pre {
      height: 100%;
      overflow-y: auto !important;
      padding: 24px !important;
      margin: 0;
      font-family: 'Fira Code', 'Dank Mono', monospace;
      font-size: 14px;
      color: var(--code-text-color);
      white-space: pre-wrap;
      position: relative;
    }
  `}</style>
);


// --- COMPONENTS ---

const SimpleCodeBlock = ({ codeString }) => {
  const linesWithKeys = useMemo(() => {
    const lines = codeString.split('\n');
    const lineCounts = {};
    return lines.map(line => {
      const count = lineCounts[line] || 0;
      lineCounts[line] = count + 1;
      const key = `${line}-${count}`;
      return { key, line };
    });
  }, [codeString]);

  return (
    <div className="code-container">
      <pre>
        <code>
          <AnimatePresence>
            {linesWithKeys.map(({ key, line }) => (
              <motion.div
                key={key}
                layout="position"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                {line === '' ? '\u00A0' : line}
              </motion.div>
            ))}
          </AnimatePresence>
        </code>
      </pre>
    </div>
  );
};


const LeftPane = ({ activeSectionId, onMenuClick }) => (
  <aside className="left-pane">
    <h2><Code size={24} /> React Counter Tutorial</h2>
    <nav>
      <ul>
        {tutorialData.map(item => (
          <li
            key={item.id}
            className={item.id === activeSectionId ? 'active' : ''}
            onClick={() => onMenuClick(item.id)}
          >
            {item.title}
          </li>
        ))}
      </ul>
    </nav>
  </aside>
);

const CenterPane = ({ sectionRefs, scrollContainerRef }) => (
  <main className="center-pane" ref={scrollContainerRef}>
    {tutorialData.map(item => (
      <section
        key={item.id}
        id={item.id}
        ref={el => (sectionRefs.current[item.id] = el)}
        className="instruction-section"
      >
        <h3>{item.title}</h3>
        <p dangerouslySetInnerHTML={{ __html: item.content.replace(/\n/g, '<br />') }} />
      </section>
    ))}
  </main>
);

const RightPane = ({ activeSectionId }) => {
  const activeCode = tutorialData.find(item => item.id === activeSectionId)?.code || '';
  return (
    <aside className="right-pane">
      <SimpleCodeBlock codeString={activeCode} />
    </aside>
  );
};


// --- MAIN APP COMPONENT ---

export default function CounterPage() {
  const [activeSectionId, setActiveSectionId] = useState(tutorialData[0].id);
  const sectionRefs = useRef({});
  const scrollContainerRef = useRef(null);
  const observerRef = useRef(null);
  const isClickScrolling = useRef(false);
  
  const handleMenuClick = (id) => {
    isClickScrolling.current = true;
    setActiveSectionId(id);
    sectionRefs.current[id]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    setTimeout(() => {
        isClickScrolling.current = false;
    }, 1000); 
  };

  useEffect(() => {
    const observerCallback = (entries) => {
        if (isClickScrolling.current) return;
        
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSectionId(entry.target.id);
          }
        });
      };

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      observerCallback,
      {
        root: scrollContainerRef.current,
        rootMargin: '-40% 0px -60% 0px',
        threshold: 0,
      }
    );

    const { current: observer } = observerRef;
    const sections = Object.values(sectionRefs.current);
    sections.forEach(section => {
      if (section) observer.observe(section);
    });

    return () => {
        sections.forEach(section => {
            if(section) observer.unobserve(section);
        })
    };
  }, []);

  return (
    <>
      <GlobalStyles />
      <div className="tutorial-container">
        <LeftPane activeSectionId={activeSectionId} onMenuClick={handleMenuClick} />
        <CenterPane sectionRefs={sectionRefs} scrollContainerRef={scrollContainerRef} />
        <RightPane activeSectionId={activeSectionId} />
      </div>
    </>
  );
}

